/**
 * What a nagging-reminder notification lets the reader DO about it.
 *
 * The bell used to offer one thing — navigate to the record — so a customer who got
 * "you have not spoken to Ada for 214 days" was thrown into Ada's page to work out
 * what to do next. This module answers the prior question: given one notification row
 * (or one row of an expanded digest), which actions apply, and to what.
 *
 * It is the SINGLE parser for that, for the same reason `groupedReminder.ts` is the
 * single parser for the digest payload beside it: `notification.data` is untyped
 * JSONB, so every field is validated here rather than trusted at three call sites.
 *
 * **Everything fails soft.** This runs inside the bell's render, over rows persisted
 * by ~30 rules across 11 modules, and those rules do NOT speak one entity vocabulary
 * — `master_data.relationship_gone_quiet` emits the TABLE name (`contacts_contacts`),
 * `pipeline.lead_stale` emits a bare follow noun (`deal`), `support` emits the
 * canonical key, several emit nouns that are in no vocabulary at all (`interview`,
 * `stock_take`, `rfq_supplier`), and `third_party_services` emits no `entity_type`
 * whatsoever. So the resolver tries every space and returns null, and the throwing
 * `toLegacy`/`fromLegacy` helpers are deliberately not used: an uncaught
 * `UnknownEntityReferenceError` here would blank the whole notifications popover for
 * the majority of rules rather than degrade one row's action set.
 */

import { InteractionSubjectType } from '../../generated/api/models/interactionSubjectType';
import { LegacySpace, isEntityType, tryFromLegacy, tryToLegacy } from '../utils/entityVocabulary';

/**
 * The canonical entity types whose records accept a logged interaction, mapped to the
 * `subject_type` the interaction API takes.
 *
 * Driven off the generated `InteractionSubjectType` rather than three string literals,
 * so the value set stays the backend's. The KEYS are canonical `{module}.{entity}`
 * vocabulary keys; `reminderActions.test.ts` asserts each one still resolves, because
 * a renamed entity would otherwise silently drop its action rather than fail.
 */
export const INTERACTION_SUBJECT_BY_ENTITY_TYPE: Readonly<
  Record<string, InteractionSubjectType>
> = {
  'master_data.contact': InteractionSubjectType.contact,
  'master_data.company': InteractionSubjectType.company,
  'pipeline.pipeline_deal': InteractionSubjectType.deal,
};

/**
 * The entity types that carry a contact-cadence card, i.e. the ones for which
 * "change how often this needs contact" is a real destination.
 *
 * A deal has interactions but no cadence, which is why this is a second, smaller set
 * rather than the keys of the map above.
 */
const CADENCE_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'master_data.contact',
  'master_data.company',
]);

/**
 * The query param that sends a reader to a record's contact-cadence card.
 *
 * Both halves live here rather than at either end, because the two ends are in
 * different trees — this strip WRITES the param and `ContactCadenceCard` READS it — and
 * a constant spelled twice is a link that breaks silently: the destination page renders
 * perfectly, just scrolled to the top with the card still below the fold.
 */
export const CADENCE_FOCUS_PARAM_NAME = 'focus';
export const CADENCE_FOCUS_PARAM_VALUE = 'cadence';
export const CADENCE_FOCUS_PARAM = `${CADENCE_FOCUS_PARAM_NAME}=${CADENCE_FOCUS_PARAM_VALUE}`;

/** One reminder notification (or digest row), reduced to what the strip needs. */
export interface ReminderActionTarget {
  /**
   * The `reminder_states` row, or null when the payload carries none — an ordinary
   * (non-reminder) notification, or a digest emitted before per-item ids shipped.
   * Snooze and dismiss are withheld rather than broken when it is null.
   */
  reminderStateId: string | null;
  /**
   * Whether the framework will nag again if the condition recurs after a dismiss.
   * False means "stop reminding me" is PERMANENT for this rule, which the copy has
   * to say out loud. Absent on rows emitted before the flag shipped, and the safe
   * reading of an absent value is the one that promises less.
   */
  ruleRearms: boolean;
  /** Canonical `{module}.{entity}`, or null if no vocabulary resolves the row. */
  entityType: string | null;
  entityId: string | null;
  /** The interaction `subject_type`, or null where the record takes no interactions. */
  interactionSubject: InteractionSubjectType | null;
  /** The follow-space noun `RemindMeButton`'s vocabulary needs, or null. */
  followEntityType: string | null;
  /** True where "change the contact cadence" has somewhere to go. */
  canChangeCadence: boolean;
  /** Days since the last interaction, where the rule supplies it. */
  daysQuiet: number | null;
  /** The configured cadence in days, where the rule supplies it. */
  cadenceDays: number | null;
}

function readString(bag: Record<string, unknown>, key: string): string | null {
  const value = bag[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readCount(bag: Record<string, unknown>, key: string): number | null {
  const value = bag[key];
  // A negative day count is not a smaller number, it is a broken payload — and it
  // would render as "last interaction -3 days ago".
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * The canonical entity type for a spelling that may come from any of five spaces.
 *
 * Ordered widest-first: a canonical key is checked before any legacy index, so a row
 * already carrying `support.ticket` never round-trips. Returns null for a noun no
 * space knows, which is a normal outcome here rather than an error.
 */
export function resolveReminderEntityType(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (isEntityType(raw)) return raw;
  for (const space of [
    LegacySpace.Table,
    LegacySpace.FollowEntityType,
    LegacySpace.CommentEntityType,
    LegacySpace.ObjectType,
    LegacySpace.SharingObjectType,
  ]) {
    const resolved = tryFromLegacy(raw, space);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Read one notification (or one digest item) into an action target.
 *
 * `data` is the notification's own bag; `entityType`/`entityId` come from the row
 * being acted on — the notification's own for a single nag, the ITEM's for a digest
 * row, which is why they are parameters rather than read out of `data`.
 * `reminderStateIdOverride` is the digest item's own id for the same reason.
 */
export function readReminderActionTarget({
  data,
  entityType,
  entityId,
  reminderStateIdOverride,
}: {
  data: unknown;
  entityType: string | null | undefined;
  entityId: string | null | undefined;
  reminderStateIdOverride?: string | null;
}): ReminderActionTarget {
  const bag = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const resolved = resolveReminderEntityType(entityType);

  return {
    reminderStateId: reminderStateIdOverride ?? readString(bag, 'reminder_state_id'),
    ruleRearms: bag.rule_rearms === true,
    entityType: resolved,
    entityId: entityId && entityId.length > 0 ? entityId : null,
    interactionSubject: resolved
      ? INTERACTION_SUBJECT_BY_ENTITY_TYPE[resolved] ?? null
      : null,
    followEntityType: resolved
      ? tryToLegacy(resolved, LegacySpace.FollowEntityType)
      : null,
    canChangeCadence: resolved !== null && CADENCE_ENTITY_TYPES.has(resolved),
    daysQuiet: readCount(bag, 'days_quiet'),
    cadenceDays: readCount(bag, 'cadence_days'),
  };
}

/** The props `InteractionFormDialog` needs to log against this record. */
export interface InteractionDialogTarget {
  subjectType: InteractionSubjectType;
  subjectId: string;
  contactId?: string;
  organizationId?: string;
}

/**
 * Which id field the interaction dialog wants for this target, or null when the
 * record takes no interactions.
 *
 * A function rather than a ternary pair in JSX because the two arms are
 * interchangeable to every test that merely asserts the dialog opened — swap
 * `contactId` and `organizationId` and the dialog still renders, just against
 * nothing. Here the mapping is a value a test can read.
 */
export function interactionDialogTarget(
  target: ReminderActionTarget,
): InteractionDialogTarget | null {
  if (!target.interactionSubject || !target.entityId) return null;
  const base = { subjectType: target.interactionSubject, subjectId: target.entityId };
  switch (target.interactionSubject) {
    case InteractionSubjectType.contact:
      return { ...base, contactId: target.entityId };
    case InteractionSubjectType.company:
      return { ...base, organizationId: target.entityId };
    default:
      // A deal names its subject and nothing else — it is neither.
      return base;
  }
}

/**
 * True when this row is a NAG, i.e. when the bell should disclose a strip instead of
 * navigating.
 *
 * The test is the reminder state id alone, deliberately — NOT "does this row have any
 * applicable action". The record-scoped capabilities (log an activity, change the
 * cadence) are properties of the ENTITY, so a comment mention on a contact carries them
 * too, and keying the click contract on them would silently turn every contact-shaped
 * notification in the product into a disclosure. Only a reminder has a state id.
 *
 * A nag whose state has since been resolved still passes: the id is on the payload, the
 * strip looks it up, and saying "this reminder is no longer active" is the honest answer
 * to a click on it. That is a question for the strip, not for the click contract.
 */
export function hasReminderActions(target: ReminderActionTarget): boolean {
  return target.reminderStateId !== null;
}
